import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { API_APPROVAL_DASHBOARD_STATS } from "../../../../../config/Endpoints.js";
import { showError } from "../../../../utils/toast.ts";
import type { ApprovalDashboardFilters, ApprovalStats } from "../types.ts";

const DEFAULT_STATS: ApprovalStats = {
  totalTimesheets: 0,
  approvedTotal: 0,
  pendingTotal: 0,
  rejectedTotal: 0,
};

function buildStatsParams(
  filters: ApprovalDashboardFilters,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filters.approverId != null) params.approverId = filters.approverId;
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.region) params.region = filters.region;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  return params;
}

export function useApprovalDashboardStats(filters: ApprovalDashboardFilters) {
  const [stats, setStats] = useState<ApprovalStats>(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const token = Cookies.get("accessToken");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<Record<string, unknown>>(
        API_APPROVAL_DASHBOARD_STATS,
        {
          params: buildStatsParams(filters),
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const root = (res.data ?? {}) as Record<string, unknown>;
      // Some APIs nest the payload under `data`.
      const body =
        root && typeof root.data === "object" && root.data !== null
          ? (root.data as Record<string, unknown>)
          : root;

      const num = (...keys: string[]): number => {
        for (const k of keys) {
          const v = body[k];
          if (v != null && Number.isFinite(Number(v))) return Number(v);
        }
        return 0;
      };

      setStats({
        totalTimesheets: num(
          "totalTimesheets",
          "total",
          "totalCount",
          "totalRecords",
        ),
        approvedTotal: num(
          "approvedTotal",
          "approved",
          "approvedCount",
          "totalApproved",
        ),
        pendingTotal: num(
          "pendingTotal",
          "pending",
          "pendingCount",
          "totalPending",
        ),
        rejectedTotal: num(
          "rejectedTotal",
          "rejected",
          "rejectedCount",
          "totalRejected",
        ),
      });
    } catch (err) {
      setStats(DEFAULT_STATS);
      const msg =
        (axios.isAxiosError(err) && err.response?.data?.message) ||
        (err instanceof Error ? err.message : null) ||
        "Failed to load approval stats.";
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
}
